import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Spinner, Button, Badge, Alert } from "react-bootstrap";
import ReviewForm from "./ReviewForm";
import ReviewList from "./ReviewList";
import { usePageTitle } from '../context/PageTitleContext';
import { useAuth } from '../context/AuthContext';
import { isGuestSaved, toggleGuestSave } from '../hooks/useGuestSaves';
import { generateSlug } from '../utils/slugUtils';
import { renderRichText } from '../utils/richText';
import AddDishCategory from './AddDishCategory';
import { normalizeCrop, croppedImageStyle } from './CroppedImage';
import { dishPriceSummary, amount, unitSuffix } from '../utils/priceDisplay';

function FoodDetail() {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const { token } = useAuth(); // dish favorite/wishlist are for signed-in users (#102)
  const [food, setFood] = useState(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [restaurantsServing, setRestaurantsServing] = useState([]);
  const [foodImages, setFoodImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restaurantsLoading, setRestaurantsLoading] = useState(false);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshReviews, setRefreshReviews] = useState(0);
  // Overall rating for this dish across all restaurants (#125), shown as a
  // headline at the top so it isn't buried in the per-restaurant grid / reviews.
  const [aggregation, setAggregation] = useState(null);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  const fetchRestaurantsServing = async (foodId) => {
    setRestaurantsLoading(true);
    try {
      // "Best places for this dish" (#102 rung-3): restaurants serving this food,
      // ranked by that dish's rating there. Falls back to the plain unranked list
      // on an older backend.
      // #138: owner-aware. The token is what makes the caller's OWN private
      // pairings appear as extra rows — sent anonymously the server has no idea
      // who is asking and returns the public list, so the feature is silently
      // inert. Same trap as #141/#142: an endpoint going ownership-aware changes
      // nothing until every caller identifies itself, and it fails by showing
      // less rather than by erroring. Anonymous stays valid: public ranking.
      const ranked = await fetch(`${apiBaseUrl}/foods/${foodId}/best-places`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (ranked.ok) {
        const rows = await ranked.json();
        // Flatten to the restaurant object the grid already renders, carrying the
        // dish rating alongside so the card can show it.
        setRestaurantsServing(rows.map(row => ({
          ...row.restaurant,
          dishRating: row.dish_average_rating,
          dishReviewCount: row.dish_review_count,
          dishPrice: row.dish_price,  // #131: this restaurant's price for the dish
          // #211: the amount alone can't be rendered honestly — it is the
          // CHEAPEST variant, and it may be priced per pound.
          dishPriceUnit: row.dish_price_unit,
          dishPriceHasVariants: row.dish_price_has_variants,
          // #219: the size, when this place prices exactly one. Unlike a menu
          // item this row carries only a scalar price, so the size can't be
          // derived here — the server names it.
          dishPriceSize: row.dish_price_size,
          // #138: one of YOUR private pairings, merged into the list but never
          // into its statistics. The card must badge it — unbadged, your own
          // rating reads as public consensus (#136).
          isOwnPrivate: row.is_own_private === true,
        })));
        return;
      }

      const response = await fetch(`${apiBaseUrl}/foods/${foodId}/restaurants`);
      if (!response.ok) {
        // If neither endpoint exists, just skip showing restaurants
        console.log("Restaurants serving endpoint not available");
        setRestaurantsServing([]);
        return;
      }
      const data = await response.json();
      setRestaurantsServing(data);
    } catch (error) {
      console.error("Error fetching restaurants:", error);
      setRestaurantsServing([]);
    } finally {
      setRestaurantsLoading(false);
    }
  };

  const fetchFoodImages = async (foodId) => {
    setImagesLoading(true);
    try {
      // Get all menu items for this food to collect images.
      //
      // #261: owner-aware now. Anonymously this returns public items only, so a
      // dish that exists only at your own private place comes back empty and the
      // gallery renders as "no photos" — which is exactly what it did before the
      // server fix, because the server fix alone cannot tell who is asking.
      // Third instance of the same trap today (#138 here and on best-places,
      // #141/#142 originally): an endpoint going ownership-aware changes nothing
      // until every caller identifies itself, and it fails by showing LESS rather
      // than by erroring.
      //
      // The effect that calls this already lists `token` in its dependencies
      // (added for #138), so signing in or out re-runs it. Both halves matter.
      const response = await fetch(`${apiBaseUrl}/foods/menu/items/?food_id=${foodId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        console.log("Menu items endpoint not available");
        setFoodImages([]);
        return;
      }
      const menuItems = await response.json();
      console.log("Menu items for this food:", menuItems);

      // Collect all images from all menu items
      const allImages = [];
      menuItems.forEach(item => {
        if (item.images && item.images.length > 0) {
          item.images.forEach(image => {
            allImages.push({
              ...image,
              restaurantName: item.restaurant_name || 'Unknown Restaurant',
              customName: item.custom_name || item.food?.name || 'Unknown Food'
            });
          });
        }
      });

      // Sort by primary first, then by display_order
      allImages.sort((a, b) => {
        if (a.is_primary !== b.is_primary) {
          return b.is_primary - a.is_primary; // Primary first
        }
        return a.display_order - b.display_order;
      });

      setFoodImages(allImages);
    } catch (error) {
      console.error("Error fetching food images:", error);
      setFoodImages([]);
    } finally {
      setImagesLoading(false);
    }
  };

  useEffect(() => {
    // Fetch food details
    fetch(`${apiBaseUrl}/foods/${id}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log("Received food details:", data);
        setFood(data);
        setPageTitle(data.name); // Set page title for navbar
        setLoading(false);

        // Fetch restaurants serving this food
        fetchRestaurantsServing(data.id);

        // Fetch images for this food from menu items
        fetchFoodImages(data.id);
      })
      .catch((error) => {
        console.error("Error fetching food details:", error);
        setError("Failed to load food details");
        setLoading(false);
      });
    // #138: `token` is a dependency, not noise. The best-places fetch inside is
    // owner-aware now, so signing in (or out) has to re-ask — otherwise your own
    // private places appear only after a manual reload, and signing out would
    // leave them on screen. That second half is what made #141's server fix inert
    // until the client sent the token AND re-ran on it.
  }, [id, apiBaseUrl, setPageTitle, token]);

  // Overall dish rating (#125), refetched when a review is added.
  useEffect(() => {
    if (!id) return;
    let active = true;
    fetch(`${apiBaseUrl}/reviews/stats/aggregated?food_id=${id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active) setAggregation(d); })
      .catch(() => {});
    return () => { active = false; };
  }, [id, apiBaseUrl, refreshReviews]);

  // Dish favorite/wishlist (#102). Signed-in users hit the API; guests save
  // locally (merged into the account on login — same model as restaurant saves, #13).
  const authedFetch = (url, options = {}) =>
    fetch(url, { ...options, headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` } });

  // Local snapshot for a guest dish save (target_type distinguishes it from a restaurant).
  const buildFoodSnapshot = () => ({
    id: food?.id,
    target_type: 'food',
    name: food?.name,
    image: foodImages?.[0]?.flickr_url || null,
  });

  useEffect(() => {
    if (!food?.id) return;
    // Guests reflect their localStorage-backed dish saves.
    if (!token) {
      setIsFavorited(isGuestSaved('favorites', food.id, 'food'));
      setIsInWishlist(isGuestSaved('wishlist', food.id, 'food'));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [favRes, wishRes] = await Promise.all([
          authedFetch(`${apiBaseUrl}/users/favorites/check/food/${food.id}`),
          authedFetch(`${apiBaseUrl}/users/wishlist/check/food/${food.id}`),
        ]);
        if (cancelled) return;
        if (favRes.ok) setIsFavorited((await favRes.json()).is_favorited);
        if (wishRes.ok) setIsInWishlist((await wishRes.json()).is_in_wishlist);
      } catch (err) {
        console.error('Error checking dish save status:', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, food?.id, apiBaseUrl]);

  const toggleFavorite = async () => {
    if (!food?.id) return;
    if (!token) {
      setIsFavorited(toggleGuestSave('favorites', buildFoodSnapshot()));
      return;
    }
    setFavoritesLoading(true);
    try {
      if (isFavorited) {
        const res = await authedFetch(`${apiBaseUrl}/users/favorites/food/${food.id}`, { method: 'DELETE' });
        if (res.ok || res.status === 204) setIsFavorited(false);
      } else {
        const res = await authedFetch(`${apiBaseUrl}/users/favorites/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ food_id: food.id }),
        });
        if (res.ok) setIsFavorited(true);
      }
    } catch (err) {
      console.error('Error toggling dish favorite:', err);
    } finally {
      setFavoritesLoading(false);
    }
  };

  const toggleWishlist = async () => {
    if (!food?.id) return;
    if (!token) {
      setIsInWishlist(toggleGuestSave('wishlist', buildFoodSnapshot()));
      return;
    }
    setWishlistLoading(true);
    try {
      if (isInWishlist) {
        const res = await authedFetch(`${apiBaseUrl}/users/wishlist/food/${food.id}`, { method: 'DELETE' });
        if (res.ok || res.status === 204) setIsInWishlist(false);
      } else {
        const res = await authedFetch(`${apiBaseUrl}/users/wishlist/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ food_id: food.id }),
        });
        if (res.ok) setIsInWishlist(true);
      }
    } catch (err) {
      console.error('Error toggling dish wishlist:', err);
    } finally {
      setWishlistLoading(false);
    }
  };

  if (loading) {
    return (
      <Container fluid style={{ padding: "20px" }}>
        <div className="d-flex justify-content-center align-items-center" style={{ height: "60vh" }}>
          <Spinner animation="border" />
        </div>
      </Container>
    );
  }

  if (error || !food) {
    return (
      <Container fluid style={{ padding: "20px" }}>
        <Alert variant="danger">
          {error || "Food not found"}
        </Alert>
        <Button variant="outline-secondary" onClick={() => navigate(-1)}>
          ← Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="food-detail" style={{ padding: "20px" }}>
      {/* Compact Header with inline categories/cuisines + dish save actions */}
      <div className="mb-4 d-flex justify-content-between align-items-start gap-2">
        {/* Inline Categories and Cuisines */}
        <div className="d-flex flex-wrap gap-2 align-items-center">
          {food.categories && food.categories.length > 0 && food.categories.map((category) => (
            <Badge
              key={category.id}
              bg="primary"
              pill
              style={{ cursor: 'pointer', fontSize: '0.75rem' }}
              onClick={() => navigate(`/food-category/${category.id}/${generateSlug(category.name)}`)}
            >
              {category.name}
            </Badge>
          ))}
          {food.cuisines && food.cuisines.length > 0 && food.cuisines.map((cuisine) => (
            <Badge
              key={cuisine.id}
              bg="success"
              pill
              style={{ cursor: 'pointer', fontSize: '0.75rem' }}
              onClick={() => navigate(`/cuisine/${cuisine.id}/${generateSlug(cuisine.name)}`)}
            >
              {cuisine.name}
            </Badge>
          ))}
          {/* #127b: let a signed-in user suggest a real category for this dish
              (admin-moderated). Sits alongside the existing category badges. */}
          <AddDishCategory foodId={food.id} existingCategories={food.categories || []} />
        </div>

        {/* Dish favorite / wishlist (#102) — available to guests too (saved
            locally and merged into the account on login; same as restaurants) */}
        {(
          <div className="d-flex gap-2" style={{ flexShrink: 0 }}>
            <Button
              variant={isFavorited ? "danger" : "outline-danger"}
              size="sm"
              onClick={toggleFavorite}
              disabled={favoritesLoading}
              title={isFavorited ? "Remove dish from favorites" : "Add dish to favorites"}
              style={{ fontSize: '1.5rem', padding: '0.25rem 0.5rem', lineHeight: '1', border: 'none', background: 'transparent' }}
              className={isFavorited ? 'text-danger' : 'text-muted'}
            >
              {favoritesLoading ? <Spinner animation="border" size="sm" /> : (isFavorited ? "❤️" : "🤍")}
            </Button>
            <Button
              variant={isInWishlist ? "primary" : "outline-primary"}
              size="sm"
              onClick={toggleWishlist}
              disabled={wishlistLoading}
              title={isInWishlist ? "Remove dish from wishlist" : "Add dish to wishlist"}
              style={{ fontSize: '1.5rem', padding: '0.25rem 0.5rem', lineHeight: '1', border: 'none', background: 'transparent' }}
              className={isInWishlist ? 'text-primary' : 'text-muted'}
            >
              {wishlistLoading ? <Spinner animation="border" size="sm" /> : (isInWishlist ? "📌" : "📍")}
            </Button>
          </div>
        )}
      </div>

      {/* Overall dish rating headline (#125) — aggregates this dish across all
          restaurants so it isn't buried in the grid/reviews. The dish name is
          already shown in the navbar page title, so it's not repeated here. */}
      <div className="mb-4">
        {aggregation && aggregation.total_reviews > 0 ? (
          <div className="d-flex align-items-center gap-2">
            <span style={{ color: '#f0a500', fontSize: '1.35rem', lineHeight: 1 }}>★</span>
            <span className="h5 mb-0">{aggregation.average_rating.toFixed(1)}</span>
            <span className="text-muted">
              · {aggregation.total_reviews} review{aggregation.total_reviews !== 1 ? 's' : ''}
            </span>
          </div>
        ) : (
          <div className="text-muted small">No ratings yet</div>
        )}
      </div>

      <Row>
        <Col md={8}>
          {/* Photo Gallery - Full Focus */}
          <Card className="mb-4">
            <Card.Body>
              {imagesLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                  <div className="mt-2 text-muted">Loading photos...</div>
                </div>
              ) : foodImages.length > 0 ? (
                <Row className="g-2">
                  {foodImages.map((image, index) => {
                    // #234: these rows are menu item images rolled up across
                    // restaurants, so a bridged table shot arrives carrying the
                    // crop for the dish it was attributed to. The frame below is
                    // already relative + overflow-hidden, so the crop styles just
                    // replace the fill-the-frame ones. No crop = whole image.
                    const crop = normalizeCrop(image);
                    return (
                    <Col key={image.id} xs={6} sm={6} md={4} lg={3} className="p-1">
                      <div className="position-relative" style={{ width: '100%', paddingBottom: '100%', overflow: 'hidden', borderRadius: '8px' }}>
                        <img
                          src={image.flickr_url}
                          alt={image.caption || `${food.name} photo ${index + 1}`}
                          className="shadow-sm"
                          style={{
                            cursor: 'pointer',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            // The frame clips to its own 8px radius, so a cropped
                            // image must not round its own (blown-up) edges.
                            ...(crop ? croppedImageStyle(crop) : { borderRadius: '8px' }),
                          }}
                          onClick={() => window.open(image.flickr_url, '_blank')}
                          onError={(e) => {
                            const container = e.target.closest('.col-6, .col-sm-6, .col-md-4, .col-lg-3');
                            if (container) container.style.display = 'none';
                          }}
                        />
                        {image.is_primary && (
                          <Badge bg="primary" className="position-absolute top-0 start-0 m-1" style={{ fontSize: '0.6rem', padding: '0.15rem 0.3rem' }}>
                            Featured
                          </Badge>
                        )}
                        {image.caption && (
                          <div className="position-absolute bottom-0 start-0 end-0 p-1 bg-dark bg-opacity-50" style={{ fontSize: '0.65rem' }}>
                            <small className="text-white">{image.caption}</small>
                          </div>
                        )}
                      </div>
                    </Col>
                    );
                  })}
                </Row>
              ) : (
                <div className="text-center py-5 text-muted">
                  <div className="mb-2" style={{ fontSize: '3rem' }}>📸</div>
                  <div>No photos available</div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          {/* About - Sidebar */}
          <Card className="mb-4">
            <Card.Body>
              <h5 className="mb-3">ℹ️ About</h5>
              <Card.Text className="mb-3" style={{ whiteSpace: 'pre-line' }}>{renderRichText(food.description) || "No description available"}</Card.Text>

              {/* Quick Info */}
              <Row className="g-2">
                <Col xs={6}>
                  <div className="text-center p-2 bg-light rounded">
                    <div className="small text-muted">Price</div>
                    <div className="fw-semibold text-success" style={{ fontSize: '0.9rem' }}>
                      {/* #131: price lives on the menu item (per-restaurant), so
                          summarize across the places serving this dish — a single
                          price, a range, or the food's base_price, else "Varies".
                          #211 moved the logic to a tested util and taught it about
                          units and variants: it now also returns null (→ "Varies")
                          when the places disagree on the unit, because min/maxing a
                          per-lb price against an each price is a category error. */}
                      {dishPriceSummary(
                        restaurantsServing.map(r => ({
                          dish_price: r.dishPrice,
                          dish_price_unit: r.dishPriceUnit,
                          dish_price_has_variants: r.dishPriceHasVariants,
                          dish_price_size: r.dishPriceSize,
                        })),
                        food.base_price
                      ) ?? 'Varies'}
                    </div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="text-center p-2 bg-light rounded">
                    <div className="small text-muted">Prep Time</div>
                    <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                      {food.preparation_time_minutes ? `${food.preparation_time_minutes} min` : 'Varies'}
                    </div>
                  </div>
                </Col>
                {food.calories && (
                  <Col xs={6}>
                    <div className="text-center p-2 bg-light rounded">
                      <div className="small text-muted">Calories</div>
                      <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>🔥 {food.calories}</div>
                    </div>
                  </Col>
                )}
                <Col xs={6}>
                  <div className="text-center p-2 bg-light rounded">
                    <div className="small text-muted">Dietary</div>
                    <div className="d-flex justify-content-center gap-1 mt-1">
                      {food.is_vegetarian && <span title="Vegetarian">🥬</span>}
                      {food.is_vegan && <span title="Vegan">🌱</span>}
                      {food.is_gluten_free && <span title="Gluten Free">🌾</span>}
                      {!food.is_vegetarian && !food.is_vegan && !food.is_gluten_free && <span className="text-muted small">-</span>}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Restaurants Serving This Food */}
          <Card className="sticky-top" style={{ top: '20px' }}>
            <Card.Body>
              <h5 className="mb-1">🍽️ Available At</h5>
              {restaurantsServing.some(r => r.dishReviewCount > 0) && (
                <div className="small text-muted mb-3">Ranked by how this dish is rated here</div>
              )}

              {restaurantsLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <div className="mt-2 small text-muted">Finding restaurants...</div>
                </div>
              ) : restaurantsServing.length > 0 ? (
                <>
                  <div className="mb-3 text-center">
                    <Badge bg="success" pill>{restaurantsServing.length} {restaurantsServing.length === 1 ? 'Restaurant' : 'Restaurants'}</Badge>
                  </div>
                  <Row className="g-2">
                    {restaurantsServing.map((restaurant) => {
                      // Get primary image or first image from restaurant.images
                      const primaryImage = restaurant.images?.find(img => img.is_primary) || restaurant.images?.[0];
                      const imageUrl = primaryImage?.flickr_url;

                      return (
                        <Col key={restaurant.id} xs={4}>
                          <div
                            className="text-center"
                            style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                            onClick={() => navigate(`/restaurant/${restaurant.id}/${generateSlug(restaurant.name)}`)}
                          >
                            {/* Restaurant Image */}
                            <div
                              className="mb-2 position-relative"
                              style={{
                                width: '100%',
                                paddingBottom: '100%',
                                overflow: 'hidden',
                                borderRadius: '12px',
                                backgroundColor: '#f8f9fa',
                                border: '2px solid #dee2e6'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.05)';
                                e.currentTarget.style.borderColor = '#0d6efd';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.borderColor = '#dee2e6';
                              }}
                            >
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={restaurant.name}
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '10px'
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = '<div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 2.5rem;">🍽️</div>';
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    fontSize: '2.5rem'
                                  }}
                                >
                                  🍽️
                                </div>
                              )}
                            </div>

                          {/* Restaurant Name */}
                          <div className="small fw-semibold text-primary mb-1" style={{ lineHeight: '1.2' }}>
                            {restaurant.name}
                          </div>

                          {/* Location info */}
                          {restaurant.locations && restaurant.locations.length > 0 && (
                            <div className="text-muted" style={{ fontSize: '0.7rem', lineHeight: '1.2' }}>
                              {(() => {
                                const mainLocation = restaurant.locations.find(loc => loc.is_main_location) || restaurant.locations[0];
                                return `${mainLocation.city}${mainLocation.state ? `, ${mainLocation.state}` : ''}`;
                              })()}
                            </div>
                          )}

                          {/* #138: one of your own private places. The badge is
                              not decoration — without it this row's rating reads
                              as public consensus, which is the confusion #136
                              exists to prevent. */}
                          {restaurant.isOwnPrivate && (
                            <div className="mt-1">
                              <Badge bg="secondary" style={{ fontSize: '0.62rem' }}>
                                🔒 Yours
                              </Badge>
                            </div>
                          )}

                          {/* This dish's rating here (#102 rung-3) */}
                          {restaurant.dishReviewCount > 0 && (
                            <div className="mt-1" style={{ fontSize: '0.72rem', lineHeight: '1.2' }}>
                              <span style={{ color: restaurant.isOwnPrivate ? '#6c757d' : '#f0a500' }}>★</span>{' '}
                              <span className="fw-semibold">{restaurant.dishRating.toFixed(1)}</span>
                              {/* "(2)" beside a public average means two other
                                  people agreed; beside your own it means you
                                  rated it twice. Same glyph, opposite meaning. */}
                              <span className="text-muted">
                                {restaurant.isOwnPrivate ? ' (your rating)' : ` (${restaurant.dishReviewCount})`}
                              </span>
                            </div>
                          )}

                          {/* This dish's price here (#131), qualified by #211 */}
                          {restaurant.dishPrice != null && (
                            <div className="text-success fw-semibold" style={{ fontSize: '0.72rem', lineHeight: '1.2' }}>
                              {restaurant.dishPriceHasVariants ? 'from ' : ''}
                              {amount(restaurant.dishPrice)}{unitSuffix(restaurant.dishPriceUnit)}
                            </div>
                          )}
                        </div>
                      </Col>
                      );
                    })}
                  </Row>
                </>
              ) : (
                <div className="text-center py-4 text-muted">
                  <div className="mb-2" style={{ fontSize: '2rem' }}>🍽️</div>
                  <small>Not currently available at any restaurants</small>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Reviews - Sidebar */}
          <Card className="mb-4">
            <Card.Body>
              <h5 className="mb-3">⭐ Reviews</h5>
              <ReviewForm
                foodId={parseInt(id)}
                targetName={food?.name}
                onReviewSubmitted={() => setRefreshReviews(prev => prev + 1)}
              />
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Body>
              <ReviewList
                foodId={parseInt(id)}
                refresh={refreshReviews}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default FoodDetail;